<script lang="ts">
  /**
   * The 1v1 result.
   *
   * The other screen the design set never covered. It reads entirely from the
   * room, not from a local Match: by the time both innings are in, one of them
   * was played on a different phone, and the only thing either player has in
   * common is what the server holds.
   *
   * Margins are stated the way cricket states them — by wickets when the chase
   * succeeds, by runs when it fails — because "won by 2 runs" about a
   * successful chase is wrong, not just unidiomatic.
   */
  import type { RoomView } from '../lib/room.svelte';

  interface Props {
    view: RoomView;
    onScorecard: () => void;
    onRematch: () => void;
    onHome: () => void;
  }
  let { view, onScorecard, onRematch, onHome }: Props = $props();

  const you = $derived(view.you === 'host' ? view.host : view.guest);
  const them = $derived(view.you === 'host' ? view.guest : view.host);

  const first = $derived(view.innings[0]);
  const second = $derived(view.innings[1]);

  const tied = $derived(view.winner === 'TIE');
  const youWon = $derived(view.winner === view.you);
  const decidedBySuperOver = $derived(view.superOvers.length >= 2);
  /** The round that actually settled it — the last pair played. */
  const finalSuperOver = $derived(view.superOvers.slice(-2));

  const headline = $derived.by(() => {
    if (!first || !second) return '';
    if (tied) return 'Match tied';

    // A super over does not have a margin worth stating -- it is one over, and
    // "won by 4 runs" reads as though the match was won by four runs.
    if (decidedBySuperOver) return youWon ? 'Won the super over' : 'Lost the super over';

    const chaseWon = second.runs > first.runs;
    const margin = chaseWon
      ? `${10 - second.wickets} wicket${10 - second.wickets === 1 ? '' : 's'}`
      : `${first.runs - second.runs} run${first.runs - second.runs === 1 ? '' : 's'}`;
    return `${youWon ? 'Won' : 'Lost'} by ${margin}`;
  });

  const detail = $derived.by(() => {
    if (decidedBySuperOver) {
      return view.superOverIndex > 0
        ? `after ${view.superOverIndex + 1} super overs`
        : 'level after twenty overs each';
    }
    if (!second || tied) return '';
    const ballsLeft = 120 - second.legalBalls;
    return second.runs > (first?.runs ?? 0) && ballsLeft > 0
      ? `${ballsLeft} ball${ballsLeft === 1 ? '' : 's'} to spare`
      : '';
  });

  const overs = (balls: number): string => `${Math.floor(balls / 6)}.${balls % 6}`;
  const nameFor = (side: string): string =>
    (side === view.you ? you?.teamName : them?.teamName) ?? 'Opponent';
</script>

<section class="screen active" aria-label="1 vs 1 result">
  <div class="body">
    <div class="verdict">
      <div class="big">{headline}</div>
      {#if detail}<p class="muted">{detail}</p>{/if}
    </div>

    {#if first && second}
      <div class="card">
        <div class="vs">
          <span>
            <span class:you-team={first.side === view.you} class:opp-team={first.side !== view.you}>
              {nameFor(first.side)}
            </span><br />
            <span class="s tabular">{first.runs}/{first.wickets}</span>
          </span>
          <span class="faint">{tied ? 'tied with' : second.runs > first.runs ? 'lost to' : 'beat'}</span>
          <span>
            <span class:you-team={second.side === view.you} class:opp-team={second.side !== view.you}>
              {nameFor(second.side)}
            </span><br />
            <span class="s tabular">{second.runs}/{second.wickets} ({overs(second.legalBalls)})</span>
          </span>
        </div>
      </div>
    {/if}

    {#if decidedBySuperOver && finalSuperOver.length === 2}
      <p class="eyebrow" style="margin-top:var(--space-5)">Super over</p>
      <div class="card">
        <div class="vs">
          {#each finalSuperOver as so (so.side)}
            <span>
              <span class:you-team={so.side === view.you} class:opp-team={so.side !== view.you}>
                {nameFor(so.side)}
              </span><br />
              <span class="s tabular">{so.runs}/{so.wickets}</span>
            </span>
          {/each}
        </div>
      </div>
    {/if}

    <p class="eyebrow" style="margin-top:var(--space-5)">Room {view.code}</p>
    <div class="card">
      <p style="margin:0;font-size:var(--text-sm)">
        Both sides drafted from the same squads, in the same order, with the same re-spins.
        {tied
          ? 'And it still came down to nothing.'
          : youWon
            ? 'You picked better.'
            : 'They picked better.'}
      </p>
    </div>
  </div>

  <div class="actions">
    <!--
      Their innings was played on another phone and none of it was stored. This
      rebuilds both scorecards from the decision logs the room does hold.
    -->
    <button class="btn-primary" onclick={onScorecard}>Full scorecard</button>
    <div class="row2">
      <button class="btn-secondary" onclick={onRematch}>New room</button>
      <button class="btn-ghost" onclick={onHome}>Home</button>
    </div>
  </div>
</section>
